import {
  cloneElement,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "./utils/cn";

export type TooltipPlacement = "top" | "bottom" | "start" | "end";

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  placement?: TooltipPlacement;
  className?: string;
}

/**
 * Hover/focus tooltip portaled to `document.body` and clamped inside the
 * viewport so it is not clipped by overflow-hidden ancestors.
 */
const Tooltip: FC<TooltipProps> = ({
  content,
  children,
  placement = "top",
  className,
}) => {
  const tooltipId = useId();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );

  useLayoutEffect(() => {
    if (!visible || !anchorRef.current || !tooltipRef.current) {
      setCoords(null);
      return;
    }

    const updatePosition = () => {
      if (!anchorRef.current || !tooltipRef.current) return;

      const anchor = anchorRef.current.getBoundingClientRect();
      const tip = tooltipRef.current.getBoundingClientRect();
      const margin = 8;
      const offset = 6;
      let top = 0;
      let left = 0;

      switch (placement) {
        case "bottom":
          top = anchor.bottom + offset;
          left = anchor.left + anchor.width / 2 - tip.width / 2;
          break;
        case "start":
          top = anchor.top + anchor.height / 2 - tip.height / 2;
          left = anchor.left - tip.width - offset;
          break;
        case "end":
          top = anchor.top + anchor.height / 2 - tip.height / 2;
          left = anchor.right + offset;
          break;
        case "top":
        default:
          top = anchor.top - tip.height - offset;
          left = anchor.left + anchor.width / 2 - tip.width / 2;
          break;
      }

      left = Math.min(
        Math.max(left, margin),
        window.innerWidth - tip.width - margin
      );
      top = Math.min(
        Math.max(top, margin),
        window.innerHeight - tip.height - margin
      );

      setCoords({ top, left });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, placement, content]);

  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  const trigger = cloneElement(children, { "aria-describedby": tooltipId });

  const tooltipNode = (
    <span
      ref={tooltipRef}
      role="tooltip"
      id={tooltipId}
      style={
        coords
          ? { position: "fixed", top: coords.top, left: coords.left }
          : { position: "fixed", top: 0, left: 0, visibility: "hidden" }
      }
      className={cn(
        "z-popover pointer-events-none w-max max-w-[min(18rem,calc(100vw-1rem))] whitespace-normal rounded-control bg-menu px-3 py-1.5 text-center text-xs font-medium leading-snug text-white shadow-e3",
        coords ? "opacity-100" : "opacity-0",
        className
      )}
    >
      {content}
    </span>
  );

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocusCapture={show}
        onBlurCapture={hide}
      >
        {trigger}
      </span>
      {visible && typeof document !== "undefined"
        ? createPortal(tooltipNode, document.body)
        : null}
    </>
  );
};

export default Tooltip;
